import React from "react";

import { AlertCircle } from "lucide-react";

import { Button } from "~/components/ui/button";
import { P } from "~/components/ui/typography";
import { logger } from "~/lib/logger";

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

interface PartSimilarityErrorBoundaryProps {
    children: React.ReactNode;
    onReset?: () => void;
}

// AIDEV-NOTE: Error boundary to catch rendering errors in part similarity dialog

export class PartSimilarityErrorBoundary extends React.Component<
    PartSimilarityErrorBoundaryProps,
    ErrorBoundaryState
> {
    constructor(props: PartSimilarityErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        // Update state so the next render will show the fallback UI
        logger.withError(error).error("Part similarity dialog error");
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log error and component stack
        logger
            .withError(error)
            .withMetadata({ componentStack: errorInfo.componentStack })
            .error("Part similarity dialog error boundary caught");
    }

    handleReset = () => {
        this.setState({ hasError: false, error: undefined });
        this.props.onReset?.();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                    <AlertCircle className="size-12 text-destructive" />
                    <div className="text-center space-y-2">
                        <P className="font-semibold">Something went wrong</P>
                        <P className="text-sm text-muted-foreground">
                            Unable to load part similarity suggestions
                        </P>
                        {process.env.NODE_ENV === "development" &&
                            this.state.error && (
                                <details className="mt-4 text-left">
                                    <summary className="cursor-pointer text-xs text-muted-foreground">
                                        Error details
                                    </summary>
                                    <pre className="mt-2 text-xs overflow-auto max-w-md p-2 bg-muted rounded">
                                        {this.state.error.message}
                                        {"\n"}
                                        {this.state.error.stack}
                                    </pre>
                                </details>
                            )}
                    </div>
                    <Button onClick={this.handleReset} variant="outline">
                        Try Again
                    </Button>
                </div>
            );
        }

        return this.props.children;
    }
}
